import { useEffect, useState } from "react";
import { Card, FormField, Select, Stack } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { api, ApiError } from "./api";
import { formatIp, formatUtc } from "./format";
import { Flash } from "./Flash";

interface ActivityEvent {
  id: string;
  created_at: string | null;
  action: string;
  kind: "login" | "change";
  resource_type: string;
  resource_id: string | null;
  org_id: string | null;
  summary: string | null;
  client: string | null;
  ip: string | null;
  actor_email: string | null;
  actor_name: string | null;
}

const ACTION_LABELS: Record<string, string> = {
  "auth.login": "Signed in",
  "auth.logout": "Signed out",
  "auth.sso": "Signed in with SSO",
  "platform_ai.update": "Updated AI settings",
  "platform_ai.area.update": "Updated an AI area",
  "platform_ai.mcp.update": "Updated the MCP switch",
  "billing.customer_alerts": "Updated signup emails",
  "billing.payments_toggle": "Updated payments",
  "ai.calculator_draft": "Saved AI cost figures",
};

function actionLabel(action: string): string {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  return action.replaceAll(".", " ").replaceAll("_", " ");
}

export function StaffActivityPage() {
  const [kind, setKind] = useState("all");
  const [events, setEvents] = useState<ActivityEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEvents(null);
    setError(null);
    api
      .get<{ events: ActivityEvent[] }>(`/operator/staff-activity?kind=${kind}`)
      .then((row) => setEvents(row.events ?? []))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Staff activity could not be loaded."));
  }, [kind]);

  return (
    <Stack gap={4}>
      <TipHeading
        as="h2"
        label="Staff activity"
        tip="Who signed in to Saleslox as platform staff, and which admin settings they changed. Customer CRM edits stay on each organization's own audit log."
      />
      <FormField label="Show" htmlFor="staff-activity-kind">
        <Select id="staff-activity-kind" value={kind} onChange={(event) => setKind(event.target.value)}>
          <option value="all">Sign-ins and changes</option>
          <option value="login">Sign-ins only</option>
          <option value="change">Changes only</option>
        </Select>
      </FormField>
      {error && <Flash tone="error">{error}</Flash>}
      <Card>
        {events === null && !error ? (
          <p className="admin-muted">Loading…</p>
        ) : events && events.length === 0 ? (
          <p className="admin-muted">No staff activity in this view yet.</p>
        ) : (
          <div className="admin-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>What</th>
                  <th>Where</th>
                  <th>Detail</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {(events ?? []).map((row) => (
                  <tr key={row.id}>
                    <td>{formatUtc(row.created_at)}</td>
                    <td>
                      {row.actor_name || "Staff"}
                      <div className="admin-muted">{row.actor_email}</div>
                    </td>
                    <td>{actionLabel(row.action)}</td>
                    <td>{row.client || (row.kind === "login" ? "—" : "Admin console")}</td>
                    <td>{row.summary || row.org_id || row.resource_id || "—"}</td>
                    <td>{formatIp(row.ip)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Stack>
  );
}
